import type { StatusTone } from '../components/ui/status-dot';

// Types et formateurs d'une inscription confirmée, partagés entre la liste et
// son panneau (2026-09-27). Une seule définition : la colonne et le détail
// affichent la même chose pour la même réponse.

export interface Participant {
  first_name: string;
  last_name: string;
  email: string;
  position: number;
  custom_fields: Record<string, any> | null;
}

export interface Registration {
  id: string;
  event_id: string;
  category_id: string | null;
  category_name: string | null;
  team_name: string;
  primary_contact_first_name: string;
  primary_contact_last_name: string;
  primary_contact_email: string;
  primary_contact_phone: string | null;
  status: string;
  payment_status: string | null;
  team_custom_fields: Record<string, any> | null;
  participants: Participant[];
  created_at: string;
}

export interface CustomField {
  id: string;
  label: string;
  type: string;
  required: boolean;
  scope?: 'team' | 'participant';
  options?: string[];
  order: number;
}

/** Une réponse est « remplie » : les questions optionnelles laissées vides ne
 *  gonflent ni les compteurs ni les colonnes. */
export const hasValue = (v: any): boolean => {
  if (v === null || v === undefined) return false;
  if (typeof v === 'string') return v.trim() !== '';
  if (Array.isArray(v)) return v.length > 0;
  if (v && typeof v === 'object' && 'variants' in v) {
    return Object.values(v.variants || {}).some((q: any) => Number(q) > 0);
  }
  if (typeof v === 'object') return Object.keys(v).length > 0;
  return true;
};

/** Tableau, goodie à variantes `{variants:{M:2}}` → « M ×2 », option quantifiée. */
export const formatCustomResponse = (response: any): string => {
  if (response === null || response === undefined) return '';
  if (Array.isArray(response)) return response.join(', ');
  if (typeof response === 'boolean') return response ? '✓' : '';
  if (typeof response === 'object' && 'variants' in response) {
    return Object.entries(response.variants || {})
      .filter(([, q]) => Number(q) > 0)
      .map(([opt, q]) => `${opt} ×${q}`)
      .join(', ');
  }
  if (typeof response === 'object' && 'option' in response) {
    const obj = response as { quantity?: number; option?: string };
    let out = obj.option || '';
    if (obj.quantity && obj.quantity > 1) out += ` (×${obj.quantity})`;
    return out;
  }
  return String(response);
};

/** Les états de paiement écrits par le webhook Stripe et l'inscription gratuite. */
export const paymentTone = (status: string | null): StatusTone => {
  switch (status) {
    case 'paid': case 'succeeded': case 'completed': return 'success';
    case 'free': return 'success';
    case 'pending': case 'processing': return 'warning';
    case 'refunded': case 'partially_refunded': return 'info';
    case 'failed': case 'cancelled': case 'canceled': return 'destructive';
    default: return 'neutral';
  }
};

export const paymentLabelKey = (status: string | null): string => {
  switch (status) {
    case 'paid': case 'succeeded': case 'completed': return 'registrations.list.payment.paid';
    case 'free': return 'registrations.list.payment.free';
    case 'pending': case 'processing': return 'registrations.list.payment.pending';
    case 'refunded': case 'partially_refunded': return 'registrations.list.payment.refunded';
    case 'failed': case 'cancelled': case 'canceled': return 'registrations.list.payment.failed';
    default: return 'registrations.list.payment.unknown';
  }
};

/** Les types de question dont les réponses forment un ensemble fermé, donc
 *  filtrables ; un texte libre ne l'est pas. */
export const CHOICE_TYPES = new Set(['select', 'radio', 'checkbox', 'multiselect', 'boolean', 'goodie', 'paid_option']);
